import type { Conduit, Ship, ShipModule } from "./types";

// Local copy to break ship.ts import cycle. Memoized like ship.ts's so the
// hot build/partialRebuild paths don't reallocate {w,h} per module per cell.
const _effCellsCache = new WeakMap<object, [{ w: number; h: number }, { w: number; h: number }]>();
function effectiveCells(
  def: { cells: { w: number; h: number } },
  facing: 0 | 1 | 2 | 3,
): { w: number; h: number } {
  let pair = _effCellsCache.get(def);
  if (pair === undefined) {
    const normal = { w: def.cells.w, h: def.cells.h };
    const rotated = { w: def.cells.h, h: def.cells.w };
    Object.freeze(normal);
    Object.freeze(rotated);
    pair = [normal, rotated];
    _effCellsCache.set(def, pair);
  }
  return facing % 2 === 0 ? pair[0] : pair[1];
}
const CARDS = [[1, 0], [0, 1], [-1, 0], [0, -1]] as const;

export const CONDUIT_SUBDIV = 4;

/** Packs coords in [-32768, 32767] into a 32-bit key. */
const KEY_OFFSET = 1 << 15;
export function cellKeyNum(x: number, y: number): number {
  return (((x + KEY_OFFSET) & 0xffff) << 16) | ((y + KEY_OFFSET) & 0xffff);
}

export interface ResourceNetwork {
  modules: ShipModule[];
  conduits: Conduit[];
  powered: boolean;
}

/** One-way fixed-wattage pump; source can dip below dest until ambient. */
export interface HeatPumpEdge {
  from: ShipModule;
  to: ShipModule;
  wattsOut: number;
}

/** Adjacent module pair that conducts heat. The conduction graph is static
 *  between structural changes, so all geometry/material constants are
 *  precomputed here once instead of in the per-tick O(N²) scan thermal.ts ran.
 *  Per-tick math reads these verbatim → bit-identical temperatures. */
export interface ConductionEdge {
  a: ShipModule;
  b: ShipModule;
  /** (2·condA·condB)/(condA+condB) — harmonic conductivity, constant. */
  condFactor: number;
  /** Length of the shared grid edge in cells, constant. */
  sharedCells: number;
  /** Thermal capacity (m·c) of a and b, constant. */
  capA: number;
  capB: number;
}

interface NetworkCache {
  networkOf: Map<ShipModule, ResourceNetwork>;
  networks: ResourceNetwork[];
  crewComponentOf: Map<ShipModule, ShipModule[]>;
  crewComponents: ShipModule[][];
  heatPumpEdges: HeatPumpEdge[];
  conductionEdges: ConductionEdge[];
  /** Multi-cell modules appear under every cell. */
  moduleByCell: Map<number, ShipModule>;
}

interface InternalState {
  cache: NetworkCache | null;
  fullDirty: boolean;
  dirtyNetworks: Set<ResourceNetwork>;
}

const STATE = new WeakMap<Ship, InternalState>();

const HEAT_PUMP_W = 500_000;

function getState(ship: Ship): InternalState {
  let s = STATE.get(ship);
  if (!s) {
    s = { cache: null, fullDirty: true, dirtyNetworks: new Set() };
    STATE.set(ship, s);
  }
  return s;
}

/** Force full rebuild on next access (module add/move/remove, conduit, split). */
export function markNetworksDirty(ship: Ship): void {
  const s = getState(ship);
  s.fullDirty = true;
  s.dirtyNetworks.clear();
}

/** Mark only the components containing `modules` dirty. */
export function markModulesDirty(ship: Ship, modules: Iterable<ShipModule>): void {
  const s = getState(ship);
  if (s.fullDirty) return;
  const cache = s.cache;
  if (!cache) { s.fullDirty = true; return; }
  for (const m of modules) {
    const net = cache.networkOf.get(m);
    if (net) s.dirtyNetworks.add(net);
  }
}

export function getNetworks(ship: Ship): NetworkCache {
  const s = getState(ship);
  if (!s.cache || s.fullDirty) {
    s.cache = build(ship);
    s.fullDirty = false;
    s.dirtyNetworks.clear();
    return s.cache;
  }
  if (s.dirtyNetworks.size > 0) {
    s.cache = partialRebuild(ship, s.cache, s.dirtyNetworks);
    s.dirtyNetworks.clear();
  }
  return s.cache;
}

function mainCellsOf(m: ShipModule): { gx: number; gy: number }[] {
  const def = m.def;
  const eff = effectiveCells(def, m.facing);
  const out: { gx: number; gy: number }[] = [];
  for (let dy = 0; dy < eff.h; dy++) {
    for (let dx = 0; dx < eff.w; dx++) {
      out.push({ gx: m.gx + dx, gy: m.gy + dy });
    }
  }
  return out;
}

interface UnionFind {
  parent: Int32Array;
  find(x: number): number;
  unite(a: number, b: number): void;
}

function makeUF(n: number): UnionFind {
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (x: number): number => {
    let r = x;
    while (parent[r] !== r) r = parent[r];
    while (parent[x] !== r) {
      const next = parent[x];
      parent[x] = r;
      x = next;
    }
    return r;
  };
  const unite = (a: number, b: number): void => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  };
  return { parent, find, unite };
}

function solveResourceComponents(
  liveModules: ShipModule[],
  universalConduits: Conduit[],
): { networks: ResourceNetwork[]; networkOf: Map<ShipModule, ResourceNetwork> } {
  const moduleCount = liveModules.length;
  const conduitCount = universalConduits.length;

  // UF layout: [0..moduleCount) modules, [moduleCount..) conduits.
  const uf = makeUF(moduleCount + conduitCount);

  const moduleAtMain = new Map<number, number>();
  for (let li = 0; li < moduleCount; li++) {
    const m = liveModules[li];
    const eff = effectiveCells(m.def, m.facing);
    for (let dy = 0; dy < eff.h; dy++) {
      for (let dx = 0; dx < eff.w; dx++) {
        moduleAtMain.set(cellKeyNum(m.gx + dx, m.gy + dy), li);
      }
    }
  }
  const moduleForSub = (sgx: number, sgy: number): number | undefined => {
    return moduleAtMain.get(
      cellKeyNum(Math.floor(sgx / CONDUIT_SUBDIV), Math.floor(sgy / CONDUIT_SUBDIV)),
    );
  };

  const condAtSub = new Map<number, number>();
  for (let ci = 0; ci < conduitCount; ci++) {
    const c = universalConduits[ci];
    condAtSub.set(cellKeyNum(c.sgx, c.sgy), moduleCount + ci);
  }

  for (let ci = 0; ci < conduitCount; ci++) {
    const c = universalConduits[ci];
    const ufIdx = moduleCount + ci;
    const n0 = condAtSub.get(cellKeyNum(c.sgx + 1, c.sgy));
    if (n0 !== undefined) uf.unite(ufIdx, n0);
    const n1 = condAtSub.get(cellKeyNum(c.sgx, c.sgy + 1));
    if (n1 !== undefined) uf.unite(ufIdx, n1);
    const n2 = condAtSub.get(cellKeyNum(c.sgx - 1, c.sgy));
    if (n2 !== undefined) uf.unite(ufIdx, n2);
    const n3 = condAtSub.get(cellKeyNum(c.sgx, c.sgy - 1));
    if (n3 !== undefined) uf.unite(ufIdx, n3);
    const mIdx = moduleForSub(c.sgx, c.sgy);
    if (mIdx !== undefined) uf.unite(ufIdx, mIdx);
  }

  const byRoot = new Map<number, ResourceNetwork>();
  const networkOf = new Map<ShipModule, ResourceNetwork>();
  for (let li = 0; li < moduleCount; li++) {
    const m = liveModules[li];
    const root = uf.find(li);
    let net = byRoot.get(root);
    if (!net) {
      net = { modules: [], conduits: [], powered: false };
      byRoot.set(root, net);
    }
    net.modules.push(m);
    networkOf.set(m, net);
  }

  // Orphan conduits land in their own module-less network; partial
  // rebuilds rely on this to find conduits to re-solve.
  for (let ci = 0; ci < conduitCount; ci++) {
    const root = uf.find(moduleCount + ci);
    let net = byRoot.get(root);
    if (!net) {
      net = { modules: [], conduits: [], powered: false };
      byRoot.set(root, net);
    }
    net.conduits.push(universalConduits[ci]);
  }

  return { networks: [...byRoot.values()], networkOf };
}

function buildModuleByCell(modules: ShipModule[]): Map<number, ShipModule> {
  const out = new Map<number, ShipModule>();
  for (const m of modules) {
    if (m.hp <= 0) continue;
    const eff = effectiveCells(m.def, m.facing);
    for (let dy = 0; dy < eff.h; dy++) {
      for (let dx = 0; dx < eff.w; dx++) {
        out.set(cellKeyNum(m.gx + dx, m.gy + dy), m);
      }
    }
  }
  return out;
}

function buildCrewComponents(
  liveModules: ShipModule[],
): { crewComponentOf: Map<ShipModule, ShipModule[]>; crewComponents: ShipModule[][] } {
  const crewCellMap = new Map<number, ShipModule>();
  for (const m of liveModules) {
    if (!m.def.stats.crewTraversable) continue;
    for (const { gx, gy } of mainCellsOf(m)) crewCellMap.set(cellKeyNum(gx, gy), m);
  }
  const crewVisited = new Set<ShipModule>();
  const crewComponentOf = new Map<ShipModule, ShipModule[]>();
  const crewComponents: ShipModule[][] = [];
  for (const m of liveModules) {
    if (!m.def.stats.crewTraversable) continue;
    if (crewVisited.has(m)) continue;
    const comp: ShipModule[] = [];
    const stack: ShipModule[] = [m];
    crewVisited.add(m);
    while (stack.length) {
      const cur = stack.pop() as ShipModule;
      comp.push(cur);
      crewComponentOf.set(cur, comp);
      for (const { gx, gy } of mainCellsOf(cur)) {
        for (const [vx, vy] of CARDS) {
          const n = crewCellMap.get(cellKeyNum(gx + vx, gy + vy));
          if (n && !crewVisited.has(n)) {
            crewVisited.add(n);
            stack.push(n);
          }
        }
      }
    }
    crewComponents.push(comp);
  }
  return { crewComponentOf, crewComponents };
}

function buildHeatPumpEdges(
  liveModules: ShipModule[],
  heatConduits: Conduit[],
): HeatPumpEdge[] {
  const moduleAtMain = new Map<number, ShipModule>();
  for (const m of liveModules) {
    for (const { gx, gy } of mainCellsOf(m)) moduleAtMain.set(cellKeyNum(gx, gy), m);
  }
  const moduleForSub = (sgx: number, sgy: number): ShipModule | undefined => {
    return moduleAtMain.get(
      cellKeyNum(Math.floor(sgx / CONDUIT_SUBDIV), Math.floor(sgy / CONDUIT_SUBDIV)),
    );
  };

  const heatBySub = new Map<number, Conduit>();
  for (const c of heatConduits) heatBySub.set(cellKeyNum(c.sgx, c.sgy), c);

  const heatPumpEdges: HeatPumpEdge[] = [];
  const processed = new Set<Conduit>();
  for (const seed of heatConduits) {
    if (processed.has(seed)) continue;
    const [sx, sy] = CARDS[seed.facing];

    let start = seed;
    while (true) {
      const prev = heatBySub.get(cellKeyNum(start.sgx - sx, start.sgy - sy));
      if (!prev || prev.facing !== seed.facing) break;
      start = prev;
    }
    const chain: Conduit[] = [];
    let cur: Conduit | undefined = start;
    while (cur) {
      chain.push(cur);
      processed.add(cur);
      const next = heatBySub.get(cellKeyNum(cur.sgx + sx, cur.sgy + sy));
      cur = next && next.facing === seed.facing ? next : undefined;
    }

    const end = chain[chain.length - 1];
    const sink = moduleForSub(end.sgx, end.sgy);
    if (!sink) continue;

    const sources = new Set<ShipModule>();
    for (let i = 0; i < chain.length - 1; i++) {
      const cd = chain[i];
      const m = moduleForSub(cd.sgx, cd.sgy);
      if (m && m !== sink) sources.add(m);
    }
    for (const src of sources) {
      heatPumpEdges.push({ from: src, to: sink, wattsOut: HEAT_PUMP_W });
    }
  }
  return heatPumpEdges;
}

/** Inlined to avoid a ship.ts/thermal.ts import cycle; kept byte-for-byte
 *  equivalent to ship.ts sharedEdgeCells + thermal.ts thermalCap so cached
 *  conduction matches the old per-tick scan exactly. */
function sharedEdgeCellCount(a: ShipModule, b: ShipModule): number {
  const ea = effectiveCells(a.def, a.facing);
  const eb = effectiveCells(b.def, b.facing);
  const ax0 = a.gx, ay0 = a.gy, ax1 = a.gx + ea.w, ay1 = a.gy + ea.h;
  const bx0 = b.gx, by0 = b.gy, bx1 = b.gx + eb.w, by1 = b.gy + eb.h;
  if (ax1 === bx0 || bx1 === ax0) {
    return Math.max(0, Math.min(ay1, by1) - Math.max(ay0, by0));
  }
  if (ay1 === by0 || by1 === ay0) {
    return Math.max(0, Math.min(ax1, bx1) - Math.max(ax0, bx0));
  }
  return 0;
}

function thermalCapOf(def: ShipModule["def"]): number {
  return Math.max(1, def.stats.mass) * def.stats.thermalInertia;
}

/** thermalModules = ship.modules.filter(hp > 0), order preserved — so the
 *  emitted edge order matches thermal.ts's old `for i { for j>i }` exactly,
 *  which matters because each edge mutates tempK read by later edges. */
function buildConductionEdges(thermalModules: ShipModule[]): ConductionEdge[] {
  const edges: ConductionEdge[] = [];
  for (let i = 0; i < thermalModules.length; i++) {
    const mi = thermalModules[i];
    const di = mi.def;
    const condI = di.stats.thermalConductivity;
    const capI = thermalCapOf(di);
    for (let j = i + 1; j < thermalModules.length; j++) {
      const mj = thermalModules[j];
      const sharedCells = sharedEdgeCellCount(mi, mj);
      if (sharedCells <= 0) continue;
      const condJ = mj.def.stats.thermalConductivity;
      const condSum = condI + condJ;
      if (condSum <= 0) continue;
      const condFactor = (2 * condI * condJ) / condSum;
      edges.push({ a: mi, b: mj, condFactor, sharedCells, capA: capI, capB: thermalCapOf(mj.def) });
    }
  }
  return edges;
}

function build(ship: Ship): NetworkCache {
  const liveModules = ship.modules.filter((m) => m.hp > 0 && !m.illegal);
  const conduits = ship.conduits;
  const universal = conduits.filter((c) => c.type === "universal");
  const heat = conduits.filter((c) => c.type === "heat");

  const { networks, networkOf } = solveResourceComponents(liveModules, universal);
  const moduleByCell = buildModuleByCell(liveModules);
  const { crewComponentOf, crewComponents } = buildCrewComponents(liveModules);
  const heatPumpEdges = buildHeatPumpEdges(liveModules, heat);
  // Conduction conducts through illegal-but-alive modules (matches the old
  // thermal scan, which only guarded hp>0), so use a separate hp>0 set.
  const conductionEdges = buildConductionEdges(ship.modules.filter((m) => m.hp > 0));

  return { networkOf, networks, crewComponentOf, crewComponents, heatPumpEdges, conductionEdges, moduleByCell };
}

function partialRebuild(
  ship: Ship,
  prev: NetworkCache,
  dirtyNetworks: Set<ResourceNetwork>,
): NetworkCache {
  const dirtyLiveModules: ShipModule[] = [];
  const dirtyConduits: Conduit[] = [];
  for (const net of dirtyNetworks) {
    for (const m of net.modules) {
      if (m.hp > 0) dirtyLiveModules.push(m);
    }
    for (const c of net.conduits) dirtyConduits.push(c);
  }

  const { networks: newSubNets, networkOf: subNetworkOf } =
    solveResourceComponents(dirtyLiveModules, dirtyConduits);

  // Mutate prev's collections in place rather than cloning the (potentially
  // large) networkOf / moduleByCell maps — this path runs every damage tick
  // during combat/mining. Final contents are identical to the clone version.
  const networks = prev.networks;
  let w = 0;
  for (let i = 0; i < networks.length; i++) {
    if (!dirtyNetworks.has(networks[i])) networks[w++] = networks[i];
  }
  networks.length = w;
  for (const n of newSubNets) networks.push(n);

  const networkOf = prev.networkOf;
  for (const net of dirtyNetworks) {
    for (const m of net.modules) networkOf.delete(m);
  }
  for (const [m, net] of subNetworkOf) networkOf.set(m, net);

  const moduleByCell = prev.moduleByCell;
  for (const net of dirtyNetworks) {
    for (const m of net.modules) {
      const eff = effectiveCells(m.def, m.facing);
      for (let dy = 0; dy < eff.h; dy++) {
        for (let dx = 0; dx < eff.w; dx++) {
          const k = cellKeyNum(m.gx + dx, m.gy + dy);
          if (moduleByCell.get(k) === m) moduleByCell.delete(k);
        }
      }
    }
    for (const m of net.modules) {
      if (m.hp <= 0) continue;
      const eff = effectiveCells(m.def, m.facing);
      for (let dy = 0; dy < eff.h; dy++) {
        for (let dx = 0; dx < eff.w; dx++) {
          moduleByCell.set(cellKeyNum(m.gx + dx, m.gy + dy), m);
        }
      }
    }
  }

  // Crew/heat/conduction chains can span resource networks; rebuild globally.
  const liveModules = ship.modules.filter((m) => m.hp > 0 && !m.illegal);
  const { crewComponentOf, crewComponents } = buildCrewComponents(liveModules);
  const heatPumpEdges = buildHeatPumpEdges(
    liveModules,
    ship.conduits.filter((c) => c.type === "heat"),
  );
  const conductionEdges = buildConductionEdges(ship.modules.filter((m) => m.hp > 0));

  prev.crewComponentOf = crewComponentOf;
  prev.crewComponents = crewComponents;
  prev.heatPumpEdges = heatPumpEdges;
  prev.conductionEdges = conductionEdges;
  return prev;
}
